import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableOrganizationCombobox } from '@/components/organizations/SearchableOrganizationCombobox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScopeStatusDropdown, type ScopeStatus } from './ScopeStatusDropdown';
import { Trash2, ChevronDown, ChevronUp, Building2, Star, MoreVertical, Plus, X, User, Mail, Receipt, Store, MessageSquare } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import type { ScopeContact } from '@/lib/schemas';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ProjectScope } from '@/lib/schemas';
import { AddContactModal } from '@/components/contacts/AddContactModal';
import { useOrganizationEstablishments } from '@/hooks/useOrganizationEstablishments';
import { useScopeNotes, useAddScopeNote } from '@/hooks/useScopeNotes';
import type { ScopeNote } from '@/lib/schemas';

interface ScopeCardProps {
  scope: ProjectScope;
  onDelete: (scopeId: string) => void;
  onStatusChange?: (scopeId: string, newStatus: ScopeStatus) => void;
  onUpdate?: (data: Partial<ProjectScope> & { id: string }) => void;
  organizations?: Array<{ id: string; name: string }>;
  scopeTypes?: Array<{ id: string; display_name: string; slug: string }>;
  contacts?: Array<{ id: string; name: string }>;
  onOrganizationChange?: (organizationId: string) => void;
  className?: string;
  // Phase 6 Batch 2: Accordion props
  isExpanded?: boolean;
  onToggle?: () => void;
  // Phase 7.3.3 Phase 2.5: Template support
  isTemplate?: boolean;
  // Phase 9.7bc: Set as primary client
  onSetPrimary?: (scopeId: string) => void;
  // 9.7d: Client scope count for conditional Primary badge display
  clientScopeCount?: number;
  // Phase 9.7bc: Multi-contact management
  onAddContact?: (scopeId: string, contactId: string) => void;
  onRemoveContact?: (scopeId: string, contactId: string) => void;
  onSetPrimaryContact?: (scopeId: string, contactId: string) => void;
  onSetInvoiceContact?: (scopeId: string, contactId: string) => void;
  projectId?: string; // Needed for multi-contact mutations
  // Phase 10d.3: Set of emails that have portal access (for badge display)
  portalMemberEmails?: Set<string>;
}

interface ScopeFormData {
  organization_id: string;
  scope_type_id: string;
  contact_id: string;
  establishment_id: number | null; // Phase 9.28: Optional establishment for client scopes
  status: string;
  status_notes: string;
}

/**
 * Displays a project scope card with organization, type, contact, status, and actions.
 *
 * Features:
 * - Full inline editing of all fields (Issue #13)
 * - Save/Cancel buttons appear when fields change (dirty state tracking)
 * - Subtle icon-only delete button
 * - Dynamic contact loading based on selected organization
 */
export function ScopeCard({
  scope,
  onDelete,
  onStatusChange,
  onUpdate,
  organizations,
  scopeTypes,
  contacts,
  onOrganizationChange,
  className,
  // Phase 6 Batch 2: Accordion props
  isExpanded = false,
  onToggle,
  // Phase 7.3.3 Phase 2.5: Template support
  isTemplate = false,
  // Phase 9.7bc: Set as primary client
  onSetPrimary,
  // 9.7d: Client scope count for conditional Primary badge display
  clientScopeCount = 1,
  // Phase 9.7bc: Multi-contact management
  onAddContact,
  onRemoveContact,
  onSetPrimaryContact,
  onSetInvoiceContact,
  projectId,
  portalMemberEmails,
}: ScopeCardProps) {
  // Phase 7 Batch 4: Client scopes are filtered out (shown in Project Overview)
  // Phase 7 Batch 2: Self scopes were removed from database
  // This check remains for safety but should never be true
  const isSelfScope = scope.scope_type_slug === 'self';

  // 9.7a: Check if this is a client scope for special styling (moved up for Phase 9.28 establishments query)
  const isClientScope = scope.is_client_type;

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // 9.7d: Add Contact modal state
  const [showAddContactModal, setShowAddContactModal] = useState(false);

  // R5: Scope notes state
  const [newNoteText, setNewNoteText] = useState('');
  const { notes, isLoading: isLoadingNotes } = useScopeNotes(
    projectId,
    scope.id,
    { enabled: isExpanded && !!projectId }
  );
  const { addNote, isAdding } = useAddScopeNote();

  // Issue #13: Full editing mode with react-hook-form
  const isFullEditMode = !!onUpdate && !!organizations && !!scopeTypes;

  const {
    register,
    handleSubmit,
    formState: { isDirty },
    reset,
    setValue,
    watch,
  } = useForm<ScopeFormData>({
    defaultValues: {
      organization_id: scope.organization_id || '',
      scope_type_id: scope.scope_type_id || '',
      contact_id: scope.contact_id || '', // Will be converted to '__none__' in the Select value prop
      establishment_id: scope.establishment_id ?? null, // Phase 9.28
      status: scope.status || 'not_started',
      status_notes: scope.status_notes || '',
    },
  });

  const selectedOrgId = watch('organization_id');
  useEffect(() => {
    reset({ organization_id: scope.organization_id || '', scope_type_id: scope.scope_type_id || '',
      contact_id: scope.contact_id || '', establishment_id: scope.establishment_id ?? null,
      status: scope.status || 'not_started', status_notes: scope.status_notes || '' });
  }, [scope.organization_id, scope.establishment_id, scope.contact_id, scope.status, scope.status_notes,
    scope.scope_type_id, reset]);

  // Phase 9.28: Fetch establishments for client scopes
  const { data: establishmentsData } = useOrganizationEstablishments(
    isClientScope ? selectedOrgId : undefined
  );
  const establishments = establishmentsData?.establishments || [];

  // FIX: Track previous org ID to only clear contact when USER changes dropdown (not when prop updates)
  const prevOrgIdRef = useRef(selectedOrgId);

  // Clear contact and establishment when organization changes
  useEffect(() => {
    // Only fire when user actually changes the org dropdown (not when scope.organization_id prop updates)
    if (isFullEditMode && selectedOrgId !== prevOrgIdRef.current) {
      prevOrgIdRef.current = selectedOrgId;
      setValue('contact_id', '');
      // Phase 9.28: Clear establishment when org changes
      if (isClientScope) {
        setValue('establishment_id', null);
      }
      // Notify parent to fetch contacts for new organization
      if (onOrganizationChange) {
        onOrganizationChange(selectedOrgId);
      }
    }
  }, [selectedOrgId, isFullEditMode, setValue, onOrganizationChange, isClientScope]);

  const handleSave = (data: ScopeFormData) => {
    if (onUpdate) {
      const identityChanged = isClientScope && (data.organization_id !== scope.organization_id
        || data.establishment_id !== (scope.establishment_id ?? null));
      if (identityChanged && (data.status !== scope.status || data.status_notes !== (scope.status_notes || '')
        || (data.organization_id === scope.organization_id && data.contact_id !== (scope.contact_id || '')))) {
        setEditError('Save contact, status, or notes separately before reviewing the client payer change.');
        return;
      }
      setEditError(null);
      // Phase 7.5.15: scope_type_id is NOT included - scope type is read-only after creation
      onUpdate({
        id: scope.id,
        organization_id: data.organization_id,
        contact_id: data.contact_id || null,
        establishment_id: isClientScope ? data.establishment_id : undefined, // Phase 9.28: Only for client scopes
        status: data.status,
        status_notes: data.status_notes,
      });
      // Reset form to mark as not dirty with new values
      if (!identityChanged) reset(data);
    }
  };

  const handleCancel = () => {
    setEditError(null);
    // Reset to original scope values
    reset({
      organization_id: scope.organization_id || '',
      scope_type_id: scope.scope_type_id || '',
      contact_id: scope.contact_id || '',
      establishment_id: scope.establishment_id ?? null, // Phase 9.28
      status: scope.status || 'not_started',
      status_notes: scope.status_notes || '',
    });
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(scope.id);
    setShowDeleteDialog(false);
  };

  const handleLegacyStatusChange = (newStatus: ScopeStatus) => {
    if (onStatusChange) {
      onStatusChange(scope.id, newStatus);
    }
  };

  // 9.7d/9.18j: Handler for when a new contact is created via modal
  const handleContactCreated = (contactId: string, _contactName: string, _contactEmail?: string) => {
    // Automatically add the newly created contact to this scope
    if (onAddContact) {
      onAddContact(scope.id, contactId);
    }
    setShowAddContactModal(false);
  };

  // R5: Submit a new scope note
  const handleAddNote = async () => {
    const text = newNoteText.trim();
    if (!text || !projectId) return;
    await addNote({ projectId, scopeId: scope.id, noteText: text });
    setNewNoteText('');
  };

  // Helper function to get status badge color
  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'waiting_on_us':
        return 'destructive';
      case 'waiting_on_them':
        return 'secondary';
      case 'complete':
        return 'default';
      default:
        return 'outline';
    }
  };

  // Helper function to format status text
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'not_started':
        return 'Not Started';
      case 'waiting_on_us':
        return 'Waiting on Us';
      case 'waiting_on_them':
        return 'Waiting on Them';
      case 'complete':
        return 'Complete';
      default:
        return status;
    }
  };

  // 9.7bc: Check if this is the primary client scope
  const isPrimaryScope = scope.is_primary === true;

  // 9.9a: Check if scope is "waiting on us" for ball-in-court styling
  const isWaitingOnUs = scope.status === 'waiting_on_us';

  // Render compact view (Phase 6 Batch 2)
  if (isFullEditMode && !isExpanded) {
    return (
      <>
      <Card
        className={cn(
          className,
          // 9.9a: Ball in court styling takes priority
          isWaitingOnUs && 'border-orange-500 bg-orange-50 ring-1 ring-orange-100',
          // 9.7a: Client scope gets distinct blue styling (only when not waiting_on_us)
          isClientScope && !isWaitingOnUs && 'bg-blue-50 border-blue-200 ring-1 ring-blue-100'
        )}
        data-testid={isClientScope ? 'scope-card-client-compact' : 'scope-card-compact'}
      >
        <CardContent
          className="py-3 px-4 cursor-pointer hover:shadow-md transition-shadow"
          onClick={onToggle}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Line 1: Organization name (Phase 7.3.3 Phase 2.5: N/A for templates) */}
              <h3 className={`font-semibold text-base truncate ${isTemplate ? 'text-muted-foreground italic' : ''}`}>
                {isTemplate ? 'N/A - Template' : scope.organization_name}
              </h3>

              {/* Line 2: Badges and contact */}
              <div className="flex items-center gap-2 mt-1 text-sm">
                {/* 9.7a: Client badge for client scopes */}
                {isClientScope && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">
                    <Building2 className="w-3 h-3 mr-1" />
                    Client
                  </Badge>
                )}
                {/* 9.7bc: Primary badge for primary client scope */}
                {/* 9.7d: Only show when 2+ client scopes (no need for disambiguation with just one) */}
                {isClientScope && isPrimaryScope && clientScopeCount > 1 && (
                  <Badge variant="default" className="bg-amber-500 text-white text-xs">
                    <Star className="w-3 h-3 mr-1" />
                    Primary
                  </Badge>
                )}
                {/* 9.7d: Hide scope type badge for client scopes to avoid redundancy with Client badge */}
                {!isClientScope && (
                  <Badge variant="outline" className="text-xs" data-testid="scope-type-badge">
                    {scope.scope_type_name || scope.scope_type || 'Unknown Type'}
                  </Badge>
                )}
                <span className="text-muted-foreground">•</span>
                <Badge variant={getStatusBadgeVariant(scope.status || 'not_started')} className="text-xs">
                  {getStatusLabel(scope.status || 'not_started')}
                </Badge>
                {scope.contact_name && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground truncate">{scope.contact_name}</span>
                  </>
                )}
                {/* Phase 9.28: Show establishment in compact view */}
                {isClientScope && scope.establishment_name && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground truncate flex items-center gap-1">
                      <Store className="h-3 w-3" />
                      {scope.establishment_name}
                    </span>
                  </>
                )}
              </div>

              {/* R5: Latest note preview in compact view */}
              {scope.latest_note_text && (
                <p className="text-xs text-muted-foreground mt-1.5 truncate max-w-md flex items-center gap-1" data-testid="scope-notes-preview">
                  <MessageSquare className="h-3 w-3 shrink-0" />
                  {scope.latest_note_text}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1">
              {/* 9.7bc: More actions dropdown for client scopes */}
              {isClientScope && !isPrimaryScope && onSetPrimary && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground h-8 w-8"
                      aria-label="More actions"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onSetPrimary(scope.id)}>
                      <Star className="h-4 w-4 mr-2" />
                      Set as Primary Client
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isSelfScope}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                }}
                title={
                  isSelfScope
                    ? 'Cannot delete self scope'
                    : 'Delete scope'
                }
                className="text-muted-foreground hover:text-destructive h-8 w-8"
                aria-label="Delete scope"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scope</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the scope for "{scope.organization_name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
    );
  }

  // Render full edit mode (Issue #13)
  if (isFullEditMode) {
    // Get the currently selected organization name for the heading
    const selectedOrgName = organizations?.find(org => org.id === watch('organization_id'))?.name || scope.organization_name;

    // Issue #1 FIX: Ensure current contact is always in the dropdown options
    // If scope has a contact but it's not in the contacts array (e.g., contacts not loaded yet),
    // include it as a placeholder option so the Select component can display it
    const contactsWithCurrent = contacts || [];
    const currentContactId = scope.contact_id;
    const hasCurrentContact = currentContactId && !contactsWithCurrent.find(c => c.id === currentContactId);
    const allContacts = hasCurrentContact && scope.contact_name
      ? [...contactsWithCurrent, { id: currentContactId, name: scope.contact_name }]
      : contactsWithCurrent;

    return (
      <>
      <Card
        className={cn(
          className,
          // 9.9a: Ball in court styling takes priority
          isWaitingOnUs && 'border-orange-500 bg-orange-50 ring-1 ring-orange-100',
          // 9.7a: Client scope gets distinct blue styling (only when not waiting_on_us)
          isClientScope && !isWaitingOnUs && 'bg-blue-50 border-blue-200 ring-1 ring-blue-100'
        )}
        data-testid={isClientScope ? 'scope-card-client-expanded' : 'scope-card-expanded'}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          {editError && <p role="alert" className="text-sm text-destructive">{editError}</p>}
          {/* Phase 7.5.15: Header with inline-editable organization */}
          <CardHeader data-testid="scope-card-header">
            <div className="flex items-start justify-between gap-2">
              {/* Phase 7.5.15: Organization now inline editable in header using SearchableOrganizationCombobox */}
              {isTemplate ? (
                <h3 className="text-lg font-semibold flex-1 text-muted-foreground italic">N/A - Template</h3>
              ) : (
                <div className="flex-1 min-w-0">
                  {/* MED-08: Filter organizations by scope type category for better UX */}
                  <SearchableOrganizationCombobox
                    value={watch('organization_id')}
                    onValueChange={(value) => setValue('organization_id', value, { shouldDirty: true })}
                    categoryId={scope.scope_type_id}
                    useServerSearch={true}
                    placeholder="Select client..."
                    className="w-full"
                  />
                </div>
              )}
              <div className="flex items-center gap-1 shrink-0">
                {/* 9.7a: Client badge for client scopes */}
                {isClientScope && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-sm">
                    <Building2 className="w-3 h-3 mr-1" />
                    Client
                  </Badge>
                )}
                {/* 9.7bc: Primary badge for primary client scope */}
                {/* 9.7d: Only show when 2+ client scopes */}
                {isClientScope && isPrimaryScope && clientScopeCount > 1 && (
                  <Badge variant="default" className="bg-amber-500 text-white text-sm">
                    <Star className="w-3 h-3 mr-1" />
                    Primary
                  </Badge>
                )}
                {/* Phase 7.5.15: Scope type as read-only badge in header (hide for client scopes since we show Client badge) */}
                {!isClientScope && (
                  <Badge
                    variant="outline"
                    className="text-sm"
                    data-testid="scope-type-display"
                  >
                    {scope.scope_type_name || 'Unknown Type'}
                  </Badge>
                )}
                {/* 9.7bc: More actions dropdown for client scopes */}
                {isClientScope && !isPrimaryScope && onSetPrimary && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground h-8 w-8"
                        aria-label="More actions"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onSetPrimary(scope.id)}>
                        <Star className="h-4 w-4 mr-2" />
                        Set as Primary Client
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={isSelfScope}
                  onClick={handleDeleteClick}
                  title={
                    isSelfScope
                      ? 'Cannot delete self scope'
                      : 'Delete scope'
                  }
                  className="text-muted-foreground hover:text-destructive h-8 w-8"
                  aria-label="Delete scope"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onToggle}
                  className="h-8 w-8"
                  aria-label="Collapse scope"
                >
                  <ChevronUp className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          {/* Phase 7.5.15: Form body without organization or scope type dropdowns */}
          <CardContent data-testid="scope-card-form-body">
            <div className="space-y-4">

              {/* Phase 9.7bc: Multi-Contact Management */}
              {onAddContact && onRemoveContact ? (
                <div>
                  <Label>Contacts</Label>
                  <div className="mt-2 space-y-2">
                    {/* Existing contacts list */}
                    {scope.contacts && scope.contacts.length > 0 ? (
                      scope.contacts.map((contact) => (
                        <div
                          key={contact.id}
                          className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded-md"
                          data-testid={`scope-contact-${contact.id}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <User className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="truncate font-medium">{contact.contact_name || 'Unknown'}</span>
                                {/* Phase 10d.3: Portal access indicator */}
                                {contact.contact_email && portalMemberEmails?.has(contact.contact_email.toLowerCase()) && (
                                  <Badge
                                    className="text-[10px] px-1 py-0 h-4 bg-blue-100 text-blue-700 border-blue-200 shrink-0"
                                    data-testid={`portal-access-badge-${contact.id}`}
                                  >
                                    Portal
                                  </Badge>
                                )}
                              </div>
                              {(contact.contact_email || contact.contact_phone) && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                  {contact.contact_email && (
                                    <span className="truncate" title={contact.contact_email}>
                                      {contact.contact_email}
                                    </span>
                                  )}
                                  {contact.contact_email && contact.contact_phone && <span>•</span>}
                                  {contact.contact_phone && (
                                    <span className="shrink-0">{contact.contact_phone}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {/* Primary Contact Toggle */}
                            <div className="flex items-center gap-1.5 text-sm">
                              <Checkbox
                                id={`primary-contact-${contact.id}`}
                                checked={contact.is_primary_contact}
                                onCheckedChange={() => onSetPrimaryContact?.(scope.id, contact.id)}
                                disabled={contact.is_primary_contact}
                                aria-label={`Set ${contact.contact_name} as primary contact`}
                              />
                              <label
                                htmlFor={`primary-contact-${contact.id}`}
                                className="text-muted-foreground flex items-center gap-1 cursor-pointer"
                              >
                                <User className="h-3 w-3" />
                                Primary
                              </label>
                            </div>
                            {/* Invoice Contact Toggle */}
                            <div className="flex items-center gap-1.5 text-sm">
                              <Checkbox
                                id={`invoice-contact-${contact.id}`}
                                checked={contact.is_invoice_contact}
                                onCheckedChange={() => onSetInvoiceContact?.(scope.id, contact.id)}
                                disabled={contact.is_invoice_contact}
                                aria-label={`Set ${contact.contact_name} as invoice contact`}
                              />
                              <label
                                htmlFor={`invoice-contact-${contact.id}`}
                                className="text-muted-foreground flex items-center gap-1 cursor-pointer"
                              >
                                <Receipt className="h-3 w-3" />
                                Invoice
                              </label>
                            </div>
                            {/* Remove button */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => onRemoveContact(scope.id, contact.id)}
                              aria-label={`Remove ${contact.contact_name}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">No contacts assigned</p>
                    )}

                    {/* Add Contact Dropdown - 9.7d: Show even without existing contacts for create option */}
                    {scope.organization_id && (
                      <Select
                        value=""
                        onValueChange={(contactId) => {
                          // 9.7d: Handle "Create New Contact" option
                          if (contactId === '__create_new__') {
                            setShowAddContactModal(true);
                          } else if (contactId) {
                            onAddContact(scope.id, contactId);
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Plus className="h-4 w-4" />
                            <span>Add contact...</span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {/* 9.7d: Create New Contact option */}
                          <SelectItem value="__create_new__" className="text-primary font-medium">
                            <div className="flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              Create New Contact
                            </div>
                          </SelectItem>
                          {/* Existing contacts */}
                          {allContacts && allContacts
                            .filter(c => !scope.contacts?.some(sc => sc.contact_id === c.id))
                            .map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {contact.name}
                              </SelectItem>
                            ))}
                          {allContacts && allContacts.filter(c => !scope.contacts?.some(sc => sc.contact_id === c.id)).length === 0 && (
                            <SelectItem value="__no_available__" disabled>
                              All contacts already assigned
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ) : (
                /* Legacy single contact dropdown for backward compatibility */
                <div>
                  <Label htmlFor={`contact-${scope.id}`}>Contact</Label>
                  <Select
                    value={watch('contact_id') || '__none__'}
                    onValueChange={(value) => setValue('contact_id', value === '__none__' ? '' : value, { shouldDirty: true })}
                  >
                    <SelectTrigger id={`contact-${scope.id}`}>
                      <SelectValue placeholder="Select contact (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No contact</SelectItem>
                      {allContacts?.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Phase 9.28: Establishment (DBA) Dropdown - Client scopes only */}
              {isClientScope && establishments.length > 0 && (
                <div>
                  <Label htmlFor={`establishment-${scope.id}`}>
                    <div className="flex items-center gap-1">
                      <Store className="h-4 w-4" />
                      Establishment (DBA)
                    </div>
                  </Label>
                  <Select
                    value={watch('establishment_id')?.toString() ?? '__none__'}
                    onValueChange={(value) => {
                      const establishmentId = value === '__none__' ? null : parseInt(value, 10);
                      setValue('establishment_id', establishmentId, { shouldDirty: true });
                    }}
                  >
                    <SelectTrigger id={`establishment-${scope.id}`} data-testid="scope-establishment-select">
                      <SelectValue placeholder="Select establishment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None (use organization)</SelectItem>
                      {establishments.map((est) => (
                        <SelectItem key={est.id} value={est.id.toString()}>
                          {est.name}
                          {est.is_default && <span className="ml-2 text-muted-foreground">(Default)</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select a specific DBA for invoices from this client scope
                  </p>
                </div>
              )}

              {/* Status Dropdown */}
              <div>
                <Label htmlFor={`status-${scope.id}`}>Status</Label>
                <ScopeStatusDropdown
                  id={`status-${scope.id}`}
                  value={watch('status') as ScopeStatus}
                  onChange={(newStatus) => setValue('status', newStatus, { shouldDirty: true })}
                  className="w-full"
                />
              </div>

              {/* Notes Textarea (legacy status_notes field) */}
              <div>
                <Label htmlFor={`notes-${scope.id}`}>Notes</Label>
                <Textarea
                  id={`notes-${scope.id}`}
                  {...register('status_notes')}
                  placeholder="Add notes about this scope..."
                  rows={3}
                />
              </div>

              {/* R5: Timestamped Note Log */}
              {projectId && (
                <div>
                  <Label className="flex items-center gap-1">
                    <MessageSquare className="h-4 w-4" />
                    Note History
                  </Label>

                  {/* Add note input */}
                  <div className="flex gap-2 mt-2">
                    <Textarea
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      placeholder="Add a timestamped note..."
                      rows={2}
                      className="flex-1 resize-none"
                      data-testid="scope-note-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleAddNote();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={!newNoteText.trim() || isAdding}
                      onClick={handleAddNote}
                      className="self-end"
                      data-testid="scope-note-submit"
                    >
                      Add
                    </Button>
                  </div>

                  {/* Note history list */}
                  {isLoadingNotes ? (
                    <p className="text-xs text-muted-foreground mt-2">Loading notes...</p>
                  ) : notes.length > 0 ? (
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto" data-testid="scope-note-history">
                      {notes.map((note: ScopeNote) => (
                        <div
                          key={note.id}
                          className="text-sm p-2 bg-muted/50 rounded-md"
                          data-testid={`scope-note-${note.id}`}
                        >
                          <p className="whitespace-pre-wrap">{note.note_text}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {note.created_by} · {new Date(note.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">No notes yet</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              {isDirty && (
                <div className="flex gap-2 pt-2">
                  <Button type="submit" size="sm">
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </form>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scope</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the scope for "{scope.organization_name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 9.7d: Add Contact Modal for inline contact creation */}
      {scope.organization_id && (
        <AddContactModal
          open={showAddContactModal}
          organizationId={scope.organization_id}
          organizationName={scope.organization_name}
          onOpenChange={setShowAddContactModal}
          onContactAdded={() => {/* Query invalidation handled by hook */}}
          onContactCreated={handleContactCreated}
        />
      )}
      </>
    );
  }

  // Render legacy read-only mode (for backward compatibility)
  return (
    <>
    <Card
      className={cn(
        className,
        // 9.9a: Ball in court styling takes priority
        isWaitingOnUs && 'border-orange-500 bg-orange-50 ring-1 ring-orange-100',
        // 9.7a: Client scope gets distinct blue styling (only when not waiting_on_us)
        isClientScope && !isWaitingOnUs && 'bg-blue-50 border-blue-200 ring-1 ring-blue-100'
      )}
      data-testid={isClientScope ? 'scope-card-client-legacy' : 'scope-card-legacy'}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold">{scope.organization_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              {/* 9.7a: Client badge for client scopes */}
              {isClientScope && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  <Building2 className="w-3 h-3 mr-1" />
                  Client
                </Badge>
              )}
              {/* 9.7bc: Primary badge for primary client scope */}
              {/* 9.7d: Only show when 2+ client scopes */}
              {isClientScope && isPrimaryScope && clientScopeCount > 1 && (
                <Badge variant="default" className="bg-amber-500 text-white">
                  <Star className="w-3 h-3 mr-1" />
                  Primary
                </Badge>
              )}
              {/* 9.7d: Hide scope type badge for client scopes to avoid redundancy with Client badge */}
              {!isClientScope && (
                <Badge variant="outline" data-testid="scope-type-badge">{scope.scope_type_name}</Badge>
              )}
              {!isClientScope && <span className="text-gray-300">•</span>}
              <span className="text-sm text-gray-600">
                {scope.contact_name || 'No contact'}
              </span>
            </div>
          </div>
          <Button
            variant="destructive"
            size="sm"
            disabled={isSelfScope}
            onClick={handleDeleteClick}
            title={
              isSelfScope
                ? 'Cannot delete self scope'
                : 'Delete scope'
            }
          >
            Delete
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Status Dropdown */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">
              Status
            </label>
            <ScopeStatusDropdown
              value={scope.status as ScopeStatus}
              onChange={handleLegacyStatusChange}
              className="w-full"
            />
          </div>

          {/* Status Notes */}
          {scope.status_notes && (
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Notes
              </label>
              <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                {scope.status_notes}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Delete confirmation dialog */}
    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Scope</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the scope for "{scope.organization_name}"?
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
